import { createRoot } from "react-dom/client";
import { Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import "./i18n/config";

// Defer non-critical CSS (flag-icons) to avoid render-blocking
// NOTE: requestIdleCallback is NOT available in Safari/iOS — must use safe fallback
const deferLoad = typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
  ? window.requestIdleCallback.bind(window)
  : (cb: () => void) => setTimeout(cb, 100);

deferLoad(() => {
  import('flag-icons/css/flag-icons.min.css');
});

// Patch: prevent react-helmet-async crash when browser extensions modify <head>
const origRemoveChild = Node.prototype.removeChild;
// @ts-ignore
Node.prototype.removeChild = function <T extends Node>(child: T): T {
  if (child.parentNode !== this) {
    console.warn('removeChild: node not a child — skipped (helmet/extension conflict)');
    return child;
  }
  return origRemoveChild.call(this, child) as T;
};

const origInsertBefore = Node.prototype.insertBefore;
// @ts-ignore
Node.prototype.insertBefore = function <T extends Node>(newNode: T, refNode: Node | null): T {
  if (refNode && refNode.parentNode !== this) {
    console.warn('insertBefore: ref node not a child — appending instead (helmet/extension conflict)');
    return this.appendChild(newNode) as T;
  }
  return origInsertBefore.call(this, newNode, refNode) as T;
};

// Loading fallback for Suspense
const Loading = () => (
  <div className="min-h-screen bg-blueprint-bg flex items-center justify-center">
    <div className="text-xl font-bold sketch-border p-8 bg-paper-sketch">
      Loading SG7 Laboratory...
    </div>
  </div>
);

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <Suspense fallback={<Loading />}>
      <App />
    </Suspense>
  </HelmetProvider>
);
